/**
 * SIDE-PANEL (generic)
 * Collapsible panel with fixed content width (no reflow)
 */
import { ReactNode, useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"

type Side = "left" | "right"

type SidePanelProps = {
  width?: number // px
  fitContent?: boolean
  minWidth?: number // px
  maxWidth?: number // px
  scrollClassName?: string
  defaultOpen?: boolean
  side?: Side
  children: ReactNode
}

export default function SidePanel({
  width,
  fitContent = false,
  minWidth,
  maxWidth,
  scrollClassName = "",
  defaultOpen = true,
  side = "left",
  children
}: SidePanelProps) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen])

  const collapsedWidth = 18

  // Default width: half screen (CSS, no window usage)
  // If width is provided, use px; otherwise use 50vw.
  const panelWidthStyle = fitContent ? "fit-content" : width ? `${width}px` : "50vw"

  // Translate only needs a number; if width is undefined, fallback to 0
  // (when width is undefined, we rely on CSS width and translate isn't needed to be exact in px)
  const delta = width ? width - collapsedWidth : 0
  const translate = fitContent
    ? side === "left"
      ? "calc(-100% + 18px)"
      : "calc(100% - 18px)"
    : `${side === "left" ? -delta : delta}px`

  const chevron = side === "left" ? faChevronLeft : faChevronRight

  return (
    <div className={`flex h-full z-[1] ${side === "right" ? "flex-row-reverse" : ""}`}>
      {/* retractable slot */}
      <div
        onClick={!open ? () => setOpen(true) : undefined}
        className={[
          "relative overflow-hidden light-white",
          "transition-[width] duration-300 ease-out",
          !open ? "cursor-pointer hover:bg-slate-50" : ""
        ].join(" ")}
        style={{
          width: open ? panelWidthStyle : `${collapsedWidth}px`,
          minWidth: open && minWidth ? `${minWidth}px` : undefined,
          maxWidth: open && maxWidth ? `${maxWidth}px` : undefined
        }}>
        {/* fixed-width content (no reflow) */}
        <div
          className={[
            "h-full light-white border-slate-200 flex flex-col",
            "transform-gpu transition-transform duration-300 ease-out",
            side === "left" ? "border-r" : "border-l"
          ].join(" ")}
          style={{
            width: panelWidthStyle,
            minWidth: minWidth ? `${minWidth}px` : undefined,
            maxWidth: maxWidth ? `${maxWidth}px` : undefined,
            transform: open ? "translateX(0)" : `translateX(${translate})`
          }}>
          <div className={`flex-1 ${open ? "overflow-y-auto" : "overflow-hidden"} ${scrollClassName}`}>{children}</div>
        </div>
      </div>

      {/* handle (always visible) */}
      <div className="relative shrink-0 z-[2]" style={{ width: 0 }}>
        <div className={`absolute inset-y-0 ${side === "left" ? "left-0" : "right-0"} w-px bg-slate-200`} />

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Fermer le panneau" : "Ouvrir le panneau"}
          className={`
            _hover
            absolute top-1/2 -translate-y-1/2
            grid h-8 w-8 place-items-center rounded-full
            bg-white border border-slate-200 shadow-sm
            transition-all duration-200
            hover:bg-slate-50 hover:shadow-md
            ${
              side === "left"
                ? "left-0 -translate-x-1/2 hover:-translate-x-[60%]"
                : "right-0 translate-x-1/2 hover:translate-x-[60%]"
            }
          `}>
          <FontAwesomeIcon
            icon={chevron}
            className={`text-slate-500 transition-transform duration-300 ${open ? "rotate-0" : "rotate-180"}`}
          />
        </button>
      </div>
    </div>
  )
}
